import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ToastController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

// Handhelds keep the PWA open for long stretches, so poll periodically instead
// of relying only on the check that runs when the service worker registers.
const UPDATE_POLL_INTERVAL_MS = 30 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly toastController = inject(ToastController);
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  private promptShown = false;

  initialize(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // Prompt to reload as soon as a new version is downloaded and ready to swap in.
    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => void this.promptReload());

    // Check now, on a fixed interval, and whenever the app returns to the foreground.
    this.checkForUpdate();
    setInterval(() => this.checkForUpdate(), UPDATE_POLL_INTERVAL_MS);
    this.document.addEventListener('visibilitychange', () => {
      if (this.document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    });
  }

  private checkForUpdate(): void {
    void this.swUpdate.checkForUpdate().catch(() => undefined);
  }

  private async promptReload(): Promise<void> {
    if (this.promptShown) {
      return;
    }
    this.promptShown = true;

    const toast = await this.toastController.create({
      message: this.translate.instant('Update_Available'),
      position: 'bottom',
      duration: 0,
      buttons: [{ text: this.translate.instant('Update_Reload'), role: 'reload' }],
    });
    await toast.present();

    const { role } = await toast.onDidDismiss();
    if (role === 'reload') {
      this.document.location.reload();
    } else {
      // Dismissed without reloading — allow the prompt to reappear on the next check.
      this.promptShown = false;
    }
  }
}
